import { el } from '../dom.js';
import { t } from '../i18n.js';
import { recurrenceField } from './recurrence-field.js';
import { imageField } from './image-field.js';
import { tagField } from './tag-field.js';
import { linkField } from './link-field.js';
import { facetField } from './facet-field.js';
import { placeField } from './place-field.js';

// **One set of field components, three shells.** The wizard, the tabbed editor
// and the full form all mount the nodes this module builds — never a copy of
// them, and never a second set of validation beside them. Two implementations
// of the same form is how a rule ends up enforced on one path and not on the
// other, and the path it is missing from is always the one somebody actually
// uses.
//
// The grouping is the wizard's three slides, and the editor's three tabs are
// the same three groups in the same order. That is the whole reason the shells
// can differ: what a publisher learned creating an event is where they look
// when they come back to change it.

export function eventFields({ account = null, series = null, date = null, csrf = {} } = {}) {
  const title = text({ id: 'sf-title', required: true, value: series?.title });
  // The mark lasts exactly until it is being answered.
  title.addEventListener('input', () => title.removeAttribute('aria-invalid'));
  const subtitle = text({ id: 'sf-subtitle', value: series?.subtitle });
  const description = el('textarea', { id: 'sf-description', rows: 3 });
  description.value = series?.description ?? '';

  const place = placeField({ account, series });
  const flyer = imageField({ seriesId: series?.id ?? null, csrfHeader: csrf });

  const startDate = el('input', {
    id: 'sf-date', type: 'date', required: true,
    value: series?.localStartDate ?? date ?? todayIso(),
  });
  const startTime = el('input', {
    id: 'sf-time', type: 'time', required: true,
    value: series?.localStartTime ?? '20:00',
  });
  const duration = el('input', {
    id: 'sf-duration', type: 'number', min: '15', step: '15',
    value: series?.durationMin ?? 180,
  });

  const price = text({ id: 'sf-price', value: series?.priceText });
  // A flag beside the text, not inside it. The text says what it costs; the
  // flag is what the filter can rely on, and prose cannot answer "free" for a
  // machine — "Spende" and "ab 5 €" are neither free nor a number.
  const priceFree = el('input', { id: 'sf-price-free', type: 'checkbox' });
  // Unticked by default, and reversed back to it on the day it shipped free.
  // An untouched flag would *claim* free, and price is a structured field
  // precisely so the filter does not sort by who remembered to set it —
  // "nobody said" has to stay distinguishable from "somebody said no charge".
  //
  // The field it governs still hides until it is unticked, which is what the
  // change was actually for: a price field is noise on the slide people
  // abandon, right up until it is the answer.
  priceFree.checked = Boolean(series?.priceFree);

  const priceField = field('sf-price', t.fieldPrice, price);
  const showPrice = () => { priceField.hidden = priceFree.checked; };
  priceFree.addEventListener('change', showPrice);
  showPrice();

  // The ticket link stays its own field: it earns the primary button on the
  // public page and it is what `ticket_click` counts. Everything else is a
  // labelled row in the list below.
  const ticketUrl = el('input', { id: 'sf-ticket', type: 'url', value: series?.ticketUrl ?? '' });

  const recurrence = recurrenceField({
    rule: series?.recurrence ?? { freq: 'once' },
    startDate: startDate.value,
  });
  // The calendar behind the panel marks it. An event and said day are two
  // different modules — the form knows which date, the grid knows what a day
  // looks like — so this is an announcement rather than a reach across.
  const announceDate = () => document.dispatchEvent(
    new CustomEvent('tba:event-date', { detail: { date: startDate.value || null } })
  );
  startDate.addEventListener('change', () => {
    recurrence.setStartDate(startDate.value);
    announceDate();
  });
  announceDate();

  // Structure and free vocabulary side by side, in that order: the facets are
  // what the filter narrows on, the tags are what has no structure yet.
  const facets = facetField({ initial: series?.facets ?? [] });
  const tags = tagField({ initial: series?.tags ?? [] });
  const links = linkField({ initial: series?.links ?? [], idPrefix: 'sf-link' });

  // The flyer leads because that is how the material arrives: somebody has a
  // JPG from a designer with the title, the date and the line-up printed on
  // it. Skippable, always — a failed upload must never block an event that
  // needs no artwork.
  const slides = [
    {
      key: 'what',
      label: t.stepWhat,
      nodes: [
        flyer.node,
        field('sf-title', t.fieldTitle, title),
        field('sf-subtitle', t.fieldSubtitle, subtitle),
        field('sf-description', t.fieldDescription, description),
        place.node,
      ],
    },
    {
      key: 'when',
      label: t.stepWhen,
      nodes: [
        el('div', { class: 'field-row' }, [
          field('sf-date', t.fieldStartDate, startDate),
          field('sf-time', t.fieldStartTime, startTime),
          field('sf-duration', t.fieldDuration, duration),
        ]),
        recurrence.node,
        // The flag first: it decides whether the field below it exists at all,
        // and a control that appears above the thing that governs it reads as
        // an afterthought.
        el('div', { class: 'field field-check' }, [
          priceFree, el('label', { for: 'sf-price-free' }, t.fieldPriceFree),
        ]),
        priceField,
        field('sf-ticket', t.fieldTicketUrl, ticketUrl),
      ],
    },
    {
      key: 'found',
      label: t.stepFound,
      nodes: [
        facets.node,
        tags.node,
        links.node,
        // Collaborators belong on this slide and have no table to write to
        // yet. Saying so is cheaper than a control that pretends: an invited
        // collaborator can decline, and until they accept the event states the
        // place without appearing on their page — none of which a placeholder
        // could honour.
        el('p', { class: 'card-meta' }, t.collaboratorsLater),
      ],
    },
  ];

  return {
    slides,
    setSeriesId: (id) => flyer.setSeriesId(id),

    value() {
      return {
        title: title.value.trim(),
        subtitle: subtitle.value.trim() || null,
        description: description.value.trim() || null,
        placeId: place.value(),
        tags: tags.value(),
        facets: facets.value(),
        // Free wins over whatever text is behind the hidden field. Someone who
        // typed "8 €" and then ticked free would otherwise publish both.
        priceText: priceFree.checked ? null : (price.value.trim() || null),
        priceFree: priceFree.checked,
        ticketUrl: ticketUrl.value.trim() || null,
        links: links.value(),
        localStartDate: startDate.value,
        localStartTime: startTime.value,
        durationMin: Number(duration.value) || null,
        recurrence: recurrence.value(),
      };
    },

    // What still stands between this form and a published event, in the order
    // the slides ask for it. The publish control names these rather than
    // greying itself out — a disabled button with no explanation is the
    // failure this list exists to prevent.
    missing() {
      const found = [];
      if (!title.value.trim()) found.push('title');
      if (place.required && !place.value()) found.push('place');

      const classifying = facets.value().filter((facet) => !String(facet).startsWith('setting:'));
      if (tags.value().length === 0 && classifying.length === 0) found.push('classification');
      return found;
    },

    // Which slide a missing field is on, so naming it can also lead there.
    slideOf(code) {
      if (code === 'title' || code === 'place') return 'what';
      if (code === 'date' || code === 'time') return 'when';
      return 'found';
    },

    focus(code) {
      const target = { title, place: place.node, date: startDate, time: startTime }[code];
      if (target === place.node) target.querySelector('input')?.focus();
      else target?.focus();
    },

    // Focus plus the mark: the message in the head says why, `aria-invalid`
    // on the field says where. Only the title can block — everything else is
    // a publishing condition, not a hard stop.
    invalidate(code) {
      if (code !== 'title') return;
      title.setAttribute('aria-invalid', 'true');
      title.focus();
    },
  };
}

// The five codes a publish refusal can carry, worded as the thing that is
// missing rather than as an error. Both halves read them: the button computes
// its own list from the live form, the server answers with one computed from
// the stored row, and they have to say the same words or the two disagree in
// front of the publisher.
export const MISSING_LABELS = {
  title: () => t.missingTitle,
  place: () => t.missingPlace,
  date: () => t.missingDate,
  setting: () => t.missingSetting,
  classification: () => t.missingClassification,
};

export function describeMissing(codes) {
  return t.publishMissing(codes.map((code) => (MISSING_LABELS[code] ?? (() => code))()));
}

function text(attrs) {
  const node = el('input', { type: 'text', autocomplete: 'off', ...attrs, value: undefined });
  node.value = attrs.value ?? '';
  return node;
}

function field(id, label, control) {
  return el('div', { class: 'field' }, [el('label', { for: id }, label), control]);
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
